import { ForwardedRef, MouseEventHandler } from 'react'

import { Box, Button, ButtonProps, Spacer } from '@chakra-ui/react'
import { faCalendarDay, faClose } from '@fortawesome/pro-regular-svg-icons'
import { DateTime } from 'luxon'

import { useCurrentUser } from 'src/lib/hooks/auth/useCurrentUser'

import Icon from '../../Icon/Icon'

export type DatePickerButtonProps = {
  active?: boolean
  buttonRef?: ForwardedRef<HTMLButtonElement>
  date?: Date
  onClearDate: () => void
  onClick?: React.MouseEventHandler
  title: string
} & Pick<ButtonProps, 'w'> &
  ButtonProps

const DatePickerButton = ({
  buttonRef,
  date,
  onClearDate,
  title,
  onClick,
  w,
  active = false,
  ...buttonProps
}: DatePickerButtonProps) => {
  const { brandColor } = useCurrentUser()
  const subtitle = date
    ? DateTime.fromJSDate(date).toFormat('LLL dd, yyyy')
    : 'Optional'

  const styleProps: ButtonProps = active
    ? {
        bg: brandColor,
        color: 'white',
        border: '1px solid',
        borderColor: brandColor,
      }
    : {
        bg: 'white',
        color: brandColor,
        border: '1px solid',
        borderColor: 'sandstonePlus',
        _active: { bg: 'air' },
      }

  const handleClickReset: MouseEventHandler<HTMLDivElement> = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()

    if (date) {
      onClearDate()
    }
  }

  return (
    <Button
      ref={buttonRef}
      px={4}
      paddingX={4}
      borderRadius={12}
      _hover={{ boxShadow: 'sm' }}
      onClick={onClick}
      w={w}
      {...styleProps}
      {...buttonProps}
    >
      <Box minW={0} textAlign="left">
        <Box fontSize="1.4rem">{title}</Box>
        <Box
          mt={1}
          className="body-extra-small"
          wordBreak="break-word"
          whiteSpace="break-spaces"
          w="100%"
          fontWeight={600}
          color={active ? 'white' : date ? 'onyx' : 'graphite'}
        >
          {subtitle}
        </Box>
      </Box>
      <Spacer />
      <Icon
        icon={date ? faClose : faCalendarDay}
        color={active ? 'white' : date ? 'onyx' : 'sandstonePlus'}
        ml={4}
        p={3}
        pr={0}
        fontSize={14}
        onClick={date ? handleClickReset : undefined}
      />
    </Button>
  )
}

export default DatePickerButton
